import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

import type { FulfillmentOrderDisplayStatusRoot } from '@repo/sdk'

const VALID_STATUSES: ReadonlySet<string> = new Set<FulfillmentOrderDisplayStatusRoot>([
  'PAYMENT_PENDING',
  'PAID',
  'SHIPPED',
  'DELIVERED',
  'CONFIRMED',
  'ALL_CANCELLED',
])

const DEFAULT_TAB: FulfillmentOrderDisplayStatusRoot = 'PAID'

function isDisplayStatus(value: string): value is FulfillmentOrderDisplayStatusRoot {
  return VALID_STATUSES.has(value)
}

function parseDisplayStatus(value: string | null): FulfillmentOrderDisplayStatusRoot {
  if (value && isDisplayStatus(value)) {
    return value
  }
  return DEFAULT_TAB
}

function parsePage(value: string | null): number {
  const num = Number(value)
  return Number.isInteger(num) && num >= 1 ? num : 1
}

/**
 * The query string is the single source of truth for the 판매내역 list state.
 *
 * Seeding component state from the URL once and mirroring it back made browser Back a no-op
 * and made re-clicking the menu entry with different params do nothing.
 */
export function useOrderListUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = parseDisplayStatus(searchParams.get('displayStatus'))
  const currentPage = parsePage(searchParams.get('page'))

  const setTab = useCallback(
    (tab: FulfillmentOrderDisplayStatusRoot) => {
      // A new tab always starts on page 1.
      setSearchParams({ displayStatus: tab })
    },
    [setSearchParams],
  )

  const setPage = useCallback(
    (page: number) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous)
        if (page > 1) next.set('page', String(page))
        else next.delete('page')
        return next
      })
    },
    [setSearchParams],
  )

  return { activeTab, currentPage, setTab, setPage } as const
}
